import React, { useMemo } from 'react';
import { SUBREGION_EXPORT_COLUMNS } from '../constants';
import { getPrefWarehouse } from '../utils';
import { toFullState } from '@/utilities/address';

export const useColumns = (options = {}) => {
    return useMemo(() => {
        return [
            ...SUBREGION_EXPORT_COLUMNS,
            {
                header: 'Carrier',
                value: (row) => row.carrier_id || '',
            },
            {
                header: 'Carrier Name',
                value: (row, data) => {
                    const carrier = data?.carriersById?.[row.carrier_id];
                    return carrier?.business_name || '';
                },
            },
            {
                header: 'Warehouse',
                value: (row) => row.warehouse_id || '',
            },
            {
                header: 'Warehouse Address 1',
                value: (row, data) => {
                    const locations = data?.carriersById?.[row.carrier_id]?.locations || [];
                    return getPrefWarehouse(row.warehouse_id, locations)?.business_address || '';
                },
            },
            {
                header: 'Warehouse Address 2',
                value: (row, data) => {
                    const locations = data?.carriersById?.[row.carrier_id]?.locations || [];
                    return getPrefWarehouse(row.warehouse_id, locations)?.business_unit || '';
                },
            },
            {
                header: 'Warehouse City',
                value: (row, data) => {
                    const locations = data?.carriersById?.[row.carrier_id]?.locations || [];
                    return getPrefWarehouse(row.warehouse_id, locations)?.business_city || '';
                },
            },
            {
                header: 'Warehouse State',
                value: (row, data) => {
                    const locations = data?.carriersById?.[row.carrier_id]?.locations || [];
                    return toFullState(getPrefWarehouse(row.warehouse_id, locations)?.business_state);
                },
            },
            {
                header: 'Warehouse Zip',
                value: (row, data) => {
                    const locations = data?.carriersById?.[row.carrier_id]?.locations || [];
                    return getPrefWarehouse(row.warehouse_id, locations)?.business_zip || '';
                },
            },
            ...(options?.isOnwardAdmin
                ? [
                      {
                          header: 'Direct',
                          value: (row) => !!row.is_direct,
                      },
                      {
                          header: 'MM Carrier',
                          value: (row) => row.middle_mile_carrier_id || '',
                      },
                      {
                          header: 'MM Carrier Name',
                          value: (row, data) => {
                              const carrier = data?.carriersById?.[row.middle_mile_carrier_id];
                              return carrier?.business_name || '';
                          },
                      },
                  ]
                : [
                      {
                          header: 'Site Code',
                          value: (row) => row.site_code || '',
                      },
                      {
                          header: 'Site Contact Name',
                          value: (row) => row.site_contact_name || '',
                      },
                      {
                          header: 'Site Contact Email',
                          value: (row) => row.site_contact_email || '',
                      },
                      {
                          header: 'Site Contact Phone',
                          value: (row) => row.site_contact_phone || '',
                      },
                  ]),
        ];
    }, [options?.isOnwardAdmin]);
};

// Maps a carrier_mapping slot column → the client role that may fill it.
// supply_chain_legs is unreliable, so slot eligibility keys off user.roles
// instead — see filterCarriersByLeg.
export const SLOT_TO_ROLE = {
    carrier_id: 'CARRIER',
    middle_mile_carrier_id: 'MIDDLE_MILE_CARRIER',
    warehouse_carrier_id: 'WAREHOUSE_CARRIER',
};

// UI-only sentinel for the "All order types" menu option.
export const ASSIGN_ALL_ORDER_TYPES = '__ALL__';

// What "All order types" is stored as in delivery_profile_assignments.order_type. A concrete
// value rather than NULL so the (shipper_id, order_type) unique constraint dedupes wildcard
// rows on plain columns -- which is what lets Hasura name it as an on_conflict target. 'all'
// is reserved: it must never become a real order type (guarded in __test__/utils.test.js).
export const WILDCARD_ORDER_TYPE = 'all';

export const ASSIGN_ZIP_TYPES = [
    { value: 'DROPOFF', label: 'Dropoff' },
    { value: 'PICKUP', label: 'Pickup' },
];

export const ASSIGNABLE_ORDER_TYPES = [
    { value: 'delivery', label: 'Delivery' },
    { value: 'return', label: 'Return' },
    { value: 'exchange', label: 'Exchange' },
    { value: 'service', label: 'Service' },
    { value: 'will_call', label: 'Will Call' },
    { value: 'wh_return', label: 'Warehouse Return' },
    { value: 'wh_inventory', label: 'Warehouse Inventory' },
    { value: 'inventory', label: 'Inventory' },
];
